import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Card,
  CardContent,
  Stack
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import DocumentTypeTermsDrawer from './DocumentTypeTermsDrawer';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateUploadOutlinedSx,
  harmonateToolbarButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
} from '../../../theme/harmonateStyles';

const DocumentTypeView = () => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateResults, setDuplicateResults] = useState(null);
  const [scanningDuplicates, setScanningDuplicates] = useState(false);
  
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();

  const fetchDocumentTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/tenant/${tenantId}?category=WF`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error(`Failed to fetch document types: ${resp.status} ${resp.statusText}`);
      const data = await resp.json();
      setDocumentTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setDocumentTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (params) => {
    setSelectedDocumentType(params.row);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedDocumentType(null);
  };

  const handleScanDuplicates = async () => {
    try {
      setScanningDuplicates(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/document-type-tenant-configs/tenant/${tenantId}/scan-duplicates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) {
        // Try to parse error response
        let errorMessage = `Failed to scan duplicates: ${resp.status} ${resp.statusText}`;
        try {
          const errorData = await resp.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          // If JSON parsing fails, use default message
        }
        throw new Error(errorMessage);
      }
      
      const data = await resp.json();
      setDuplicateResults(data);
      setDuplicateDialogOpen(true);
      showSuccess('Duplicate scan completed successfully');
    } catch (err) {
      console.error('Error scanning duplicates:', err);
      showError(err.message, 'Scan Duplicates Error');
    } finally {
      setScanningDuplicates(false);
    }
  };

  useEffect(() => {
    fetchDocumentTypes();
  }, []);

  if (loading) {
    return (
      <Box sx={{ ...harmonatePageSx, minHeight: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.text }} />
        <Typography variant="body2" sx={{ ml: 2, color: ht.white }}>Loading document types...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...harmonatePageSx, minHeight: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: '500px' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load document types
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />}
          onClick={fetchDocumentTypes} 
          sx={harmonateProceedButtonSx}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const columns = [
    { 
      field: 'documentTypeName', 
      headerName: 'Document Type Name', 
      flex: 1 
    },
  ];

  return (
    <Box sx={{ ...harmonatePageSx, minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Document Type
        </Typography>
        <Button
          variant="outlined"
          startIcon={scanningDuplicates ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
          onClick={handleScanDuplicates}
          disabled={scanningDuplicates}
          sx={harmonateToolbarButtonSx}
        >
          Scan Duplicate ID Terms
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          minHeight: 0,
          mb: 3,
          height: 600,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          columnResizeMode="onEnd"
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          resizable
          rows={documentTypes.map((dt, idx) => ({
            id: dt.id || idx + 1,
            documentTypeName: dt.documentTypeName || '',
            tenantId: dt.tenantId,
            documentTypeId: dt.documentTypeId
          }))}
          columns={columns}
          rowHeight={40}
          sx={{ 
            ...harmonateDataGridSx,
            flex: 1,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
          }}
          disableSelectionOnClick={true}
          getRowId={(row) => row.id}
          onRowClick={handleRowClick}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" sx={{ mb: 2, color: ht.text }}>
                  No document types available
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={fetchDocumentTypes}
                  sx={harmonateUploadOutlinedSx}
                >
                  Refresh
                </Button>
              </Box>
            )
          }}
        />
      </Paper>

      <DocumentTypeTermsDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        selectedDocumentType={selectedDocumentType}
      />

      {/* Duplicate Terms Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Duplicate Terms Scan Results</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {duplicateResults ? (
            Object.keys(duplicateResults).length > 0 ? (
              <Stack spacing={2}>
                {Object.entries(duplicateResults).map(([term, locations]) => (
                  <Card key={term} variant="outlined" sx={{ borderLeft: `4px solid ${ht.darkBlue}`, borderRadius: ht.cardRadius, borderColor: ht.line }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: ht.text }}>
                        Term: "{term}"
                      </Typography>
                      <Typography variant="body2" gutterBottom sx={{ mb: 2, color: ht.text }}>
                        Found in {locations.length} document type(s):
                      </Typography>
                      <List dense>
                        {locations.map((location, index) => (
                          <React.Fragment key={index}>
                            <ListItem>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: ht.text }}>
                                      {location.documentTypeName}
                                    </Typography>
                                    <Chip
                                      label={location.termType === 'include' ? 'Include' : 'Exclude'}
                                      size="small"
                                      sx={{
                                        height: 24,
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        bgcolor: location.termType === 'include' ? ht.secondaryTab : ht.listBg,
                                        color: location.termType === 'include' ? ht.text : ht.text,
                                        borderRadius: ht.buttonRadius,
                                        '& .MuiChip-label': { px: 1.5 },
                                      }}
                                    />
                                  </Box>
                                }
                              />
                            </ListItem>
                            {index < locations.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Alert severity="info">
                No duplicate terms found. All terms are unique across document types.
              </Alert>
            )
          ) : (
            <Typography variant="body2" sx={{ color: ht.text }}>
              No results available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDuplicateDialogOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />
    </Box>
  );
};

export default DocumentTypeView;
